import React, { useMemo } from "react";

import { useAppSelector } from "../../../store/configureStore";
import { getReportDetails } from "../../../store/reportDetails";

import { InfraredEquipment } from "../../forms/custom-infrared/infraredEquipmentForm";
import { InfraredResult } from "../../forms/custom-infrared/infraredResultForm";

import CustomCoverPage from "../custom-general/custom-cover-page";
import CustomInfoSheet from "../custom-general/custom-info-sheet";
import Conditional from "../../common/conditional";
import CustomGeneralPhotos from "../custom-general/custom-general-photos";
import CustomSection from "../custom-general/custom-section";
import ExecutiveSummary from "../custom-visual-inspection/executive-summary";
import InfraredResultReport from "./infraredResultReport";
import InspectionOverview from "./inspectionOverview";
import InfraredEquipmentReport from "./infraredEquipmentReport";
import IsometricDrawing from "../custom-utm/isometric-drawing";
import PDFTablePage from "../../themed/pdf/pdfTablePage";
import PID from "../custom-visual-inspection/pid";
import OverviewSheet from "../custom-general/overview-sheet";

import withPDF from "../../hoc/withPDF";
import useCoverSheetOverview from "../../../hooks/useCoverSheetOverview";

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface Props {
    projectDetails: models.Project;
    reportTitle: string;
    coverData: models.CustomFVIData;
	infraredEquipmentItems: InfraredEquipment[];
	infraredResultItems: InfraredResult[];
	sketch: models.PDFImageProps[]
    executiveSummary: string;
	pidData: models.PIDData;
    drawing: models.PDFImageProps[];
    photographicData: models.PDFImageProps[];
}

const CustomInfraredReport: React.FC<Props> = ({ executiveSummary, drawing, projectDetails, infraredEquipmentItems, infraredResultItems, pidData, photographicData, sketch, coverData, reportTitle }) => {
    const { inspectionTypeName } = projectDetails;
	const { itemCountPerPage } = useAppSelector(getReportDetails);

    const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [coverData.coverData]);

	const resultsWithSerialNumber = useMemo(() => {
		return infraredResultItems.map((result, index) => ({
			id: index + 1,
			...result,
		}));
	}, [infraredResultItems]);

	const overview = useCoverSheetOverview();
	const inspectionTypeLabel = utils.getExtractedInspectionName(inspectionTypeName);

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={inspectionTypeLabel} />

			<CustomInfoSheet
				projectDetails={projectDetails}
				workOrderNumber={coverData.workOrderNumber}
				reportTitle={reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={projectDetails}
			/>

			<ExecutiveSummary summary={executiveSummary} />

			<InspectionOverview />

			{utils.getChunkedData(resultsWithSerialNumber, itemCountPerPage).map((chunk) => (
				<CustomSection sectionTitle="Result Summary">
					<div className="p-1">
						<PDFTablePage data={chunk} columns={resultSummaryColumns} />
					</div>
				</CustomSection>
			))}

			{pidData.pIDImages.map((pid, index: number) => (
				<PID
					hasPID={pidData.hasPID}
					key={index} 
					pidImages={[pid]} 
					pidNumber={pidData.pIDNumber} 
					sectionTitle="P & ID"
				/>
			))}

            <IsometricDrawing label="General Drawing" isoImages={drawing} />
            
			<InfraredEquipmentReport infraredEquipmentItems={infraredEquipmentItems} />

			<InfraredResultReport data={infraredResultItems} />

			<IsometricDrawing label="Inspection Sketch" isoImages={sketch} />

			<Conditional isVisible={photographicData.length > 0}>
				<CustomGeneralPhotos
					photos={photographicData}
					label="Photographic Record"
				/>
			</Conditional>
        </React.Fragment>
	);
};

const resultSummaryColumns: models.Column[] = [
    {
        path: 'imagingLocation',
        label: 'Location',
    },
    {
        path: 'calculatedDepositVolume',
        label: 'Calculated Volume of Sediment Deposit (m3)',
    },
    {
        path: 'maxTemperature',
        label: 'Max Temperature (C)',
    },
    {
        path: 'minTemperature',
        label: 'Min Temperature (C)',
    },
];
 
export default withPDF(CustomInfraredReport);